import { useState } from 'react';
import { createPortal } from 'react-dom';

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import './FormulaDragAndDrop.css';

const DraggableFormulaOption = ({
  optionId,
  label,
  disabled = false,
  className,
  onClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id: String(optionId),
    disabled,
    data: {
      optionId,
      label,
    },
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={className}
      disabled={disabled}
      onClick={onClick}
      style={{
        opacity: isDragging ? 0 : 1,
      }}
      {...listeners}
      {...attributes}
    >
      {label}
    </button>
  );
};

const DroppableFormulaSlot = ({
  slotId,
  className,
  disabled,
  children,
  onClick,
}) => {
  const {
    isOver,
    setNodeRef,
  } = useDroppable({
    id: `formula-slot-${slotId}`,
    disabled,
    data: {
      slotId,
      dropType: 'slot',
    },
  });

  const handleKeyDown = (event) => {
    if (
      disabled ||
      (
        event.key !== 'Enter' &&
        event.key !== ' '
      )
    ) {
      return;
    }

    event.preventDefault();
    onClick?.();
  };

  return (
    <div
      ref={setNodeRef}
      className={[
        className,
        isOver
          ? 'formula-dnd-slot-over'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={
        disabled ? undefined : onClick
      }
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      {children}
    </div>
  );
};

const DroppableOptionsPool = ({
  children,
  disabled,
}) => {
  const {
    isOver,
    setNodeRef,
  } = useDroppable({
    id: 'formula-options-pool',
    disabled,
    data: {
      dropType: 'options',
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={[
        'formula-dnd-options',
        isOver
          ? 'formula-dnd-options-over'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
};

const FormulaDragAndDrop = ({
  question,
  options = [],
  selectedAnswers = {},
  expectedAnswers = {},
  isAnswerChecked = false,
  formulaRows = [],
  onAnswersChange,
  showQuestion = true,
  formulaType,
}) => {
  const [
    activeOption,
    setActiveOption,
  ] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 8,
      },
    })
  );

  if (!question) {
    return null;
  }

  const resolvedFormulaType =
    formulaType ||
    question.formulaType ||
    'simple';

  const allowedFormulaTypes = [
    'simple',
    'right',
    'left',
  ];

  const safeFormulaType =
    allowedFormulaTypes.includes(
      resolvedFormulaType
    )
      ? resolvedFormulaType
      : 'simple';

  const safeSelectedAnswers =
    selectedAnswers &&
    typeof selectedAnswers === 'object' &&
    !Array.isArray(selectedAnswers)
      ? selectedAnswers
      : {};

  const getOptionById = (optionId) =>
    options.find(
      (item) =>
        String(item.id) ===
        String(optionId)
    );

  const getOptionLabel = (optionId) => {
    if (!optionId) {
      return '';
    }

    const option =
      getOptionById(optionId);

    return (
      option?.label ??
      option?.value ??
      option?.code ??
      ''
    );
  };

  const getOptionValue = (optionId) => {
    if (!optionId) {
      return '';
    }

    const option =
      getOptionById(optionId);

    return (
      option?.value ??
      option?.code ??
      option?.label ??
      ''
    );
  };

  const updateAnswers = (
    nextAnswers
  ) => {
    onAnswersChange?.(nextAnswers);
  };

  const removeOptionFromAllSlots = (
    currentAnswers,
    optionId
  ) => {
    const nextAnswers = {
      ...currentAnswers,
    };

    Object.keys(nextAnswers).forEach(
      (slotId) => {
        if (
          String(
            nextAnswers[slotId]
          ) === String(optionId)
        ) {
          nextAnswers[slotId] =
            null;
        }
      }
    );

    return nextAnswers;
  };

  const placeOptionInSlot = (
    slotId,
    optionId
  ) => {
    if (
      isAnswerChecked ||
      !slotId ||
      !optionId
    ) {
      return;
    }

    const nextAnswers =
      removeOptionFromAllSlots(
        safeSelectedAnswers,
        optionId
      );

    nextAnswers[slotId] =
      optionId;

    updateAnswers(nextAnswers);
  };

  const removeOptionFromSlot = (
    slotId
  ) => {
    if (
      isAnswerChecked ||
      !slotId
    ) {
      return;
    }

    updateAnswers({
      ...safeSelectedAnswers,
      [slotId]: null,
    });
  };

  const moveOptionBackToPool = (
    optionId
  ) => {
    if (
      isAnswerChecked ||
      !optionId
    ) {
      return;
    }

    const nextAnswers =
      removeOptionFromAllSlots(
        safeSelectedAnswers,
        optionId
      );

    updateAnswers(nextAnswers);
  };

  const handleOptionClick = (
    optionId
  ) => {
    if (
      isAnswerChecked ||
      !optionId
    ) {
      return;
    }

    const allSlotIds =
      formulaRows.flatMap(
        (row) =>
          Array.isArray(
            row?.slotIds
          )
            ? row.slotIds
            : []
      );

    const firstEmptySlotId =
      allSlotIds.find(
        (slotId) =>
          !safeSelectedAnswers[
            slotId
          ]
      );

    if (firstEmptySlotId) {
      placeOptionInSlot(
        firstEmptySlotId,
        optionId
      );
    }
  };

  const handleDragStart = (
    event
  ) => {
    const optionId =
      event.active.data.current
        ?.optionId ??
      event.active.id;

    const label =
      event.active.data.current
        ?.label ??
      getOptionLabel(optionId);

    setActiveOption({
      optionId,
      label,
    });
  };

  const handleDragCancel = () => {
    setActiveOption(null);
  };

  const handleDragEnd = (event) => {
    const {
      active,
      over,
    } = event;

    setActiveOption(null);

    if (
      !over ||
      isAnswerChecked
    ) {
      return;
    }

    const optionId =
      active.data.current
        ?.optionId ??
      active.id;

    const dropType =
      over.data.current
        ?.dropType;

    if (dropType === 'slot') {
      const slotId =
        over.data.current
          ?.slotId;

      placeOptionInSlot(
        slotId,
        optionId
      );

      return;
    }

    if (
      dropType === 'options'
    ) {
      moveOptionBackToPool(
        optionId
      );
    }
  };

  const renderSlot = (
    slotId,
    placeholder = ''
  ) => {
    const optionId =
      safeSelectedAnswers[
        slotId
      ] ?? null;

    const label =
      getOptionLabel(optionId);

    const value =
      getOptionValue(optionId);

    const expectedValue =
      expectedAnswers?.[
        slotId
      ] ?? '';

    const isCorrect =
      isAnswerChecked &&
      Boolean(value) &&
      String(value) ===
        String(expectedValue);

    const isIncorrect =
      isAnswerChecked &&
      Boolean(value) &&
      String(value) !==
        String(expectedValue);

    const className = [
      'formula-dnd-slot',
      label ? 'filled' : '',
      isCorrect ? 'correct' : '',
      isIncorrect
        ? 'incorrect'
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <DroppableFormulaSlot
        key={slotId}
        slotId={slotId}
        className={className}
        disabled={
          isAnswerChecked
        }
        onClick={() =>
          removeOptionFromSlot(
            slotId
          )
        }
      >
        {optionId ? (
          <DraggableFormulaOption
            optionId={optionId}
            label={label}
            disabled={
              isAnswerChecked
            }
            className="formula-dnd-slot-content"
            onClick={(event) => {
              event.stopPropagation();

              removeOptionFromSlot(
                slotId
              );
            }}
          />
        ) : (
          placeholder
        )}
      </DroppableFormulaSlot>
    );
  };

  const usedOptionIds = new Set(
    Object.values(
      safeSelectedAnswers
    )
      .filter(Boolean)
      .map(String)
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={
        handleDragStart
      }
      onDragEnd={
        handleDragEnd
      }
      onDragCancel={
        handleDragCancel
      }
    >
      <div className="formula-dnd-page">
        <div className="formula-dnd-card">
          {showQuestion &&
            question.question && (
              <div className="formula-dnd-question">
                <p>
                  {
                    question.question
                  }
                </p>
              </div>
            )}

          <div
            className={[
              'formula-dnd-board',
              `formula-dnd-board-${safeFormulaType}`,
            ].join(' ')}
          >
            {formulaRows.map(
              (
                row,
                rowIndex
              ) => {
                const sentenceParts =
                  Array.isArray(
                    row?.sentenceParts
                  )
                    ? row
                        .sentenceParts
                    : [];

                const slotIds =
                  Array.isArray(
                    row?.slotIds
                  )
                    ? row.slotIds
                    : [];

                const placeholders =
                  Array.isArray(
                    row?.placeholders
                  )
                    ? row
                        .placeholders
                    : [];

                let currentSlotIndex =
                  0;

                return (
                  <div
                    key={`formula-row-${rowIndex}`}
                    className={[
                      'formula-dnd-row',
                      `formula-dnd-row-${safeFormulaType}`,
                      row?.rowType
                        ? `formula-dnd-row-${row.rowType}`
                        : '',
                      row?.rowType
                        ? `formula-dnd-row-${safeFormulaType}-${row.rowType}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {sentenceParts.map(
                      (
                        segment,
                        segmentIndex
                      ) => {
                        const isSlot =
                          segment ===
                            '__SLOT__' ||
                          segment ===
                            '{{slot}}' ||
                          (
                            typeof segment ===
                              'string' &&
                            /^_{3,}$/.test(
                              segment
                            )
                          );

                        if (!isSlot) {
                          return (
                            <span
                              key={`formula-static-${rowIndex}-${segmentIndex}`}
                              className="formula-dnd-static"
                            >
                              {segment}
                            </span>
                          );
                        }

                        const slotId =
                          slotIds[
                            currentSlotIndex
                          ];

                        const placeholder =
                          placeholders[
                            currentSlotIndex
                          ] ?? '';

                        currentSlotIndex +=
                          1;

                        if (!slotId) {
                          return null;
                        }

                        return renderSlot(
                          slotId,
                          placeholder
                        );
                      }
                    )}
                  </div>
                );
              }
            )}
          </div>

          <DroppableOptionsPool
            disabled={
              isAnswerChecked
            }
          >
            {options.map(
              (option) => {
                const isUsed =
                  usedOptionIds.has(
                    String(option.id)
                  );

                if (isUsed) {
                  return null;
                }

                const label =
                  option.label ??
                  option.value ??
                  option.code;

                return (
                  <DraggableFormulaOption
                    key={
                      option.id
                    }
                    optionId={
                      option.id
                    }
                    label={
                      label
                    }
                    disabled={
                      isAnswerChecked
                    }
                    className="formula-dnd-option"
                    onClick={() =>
                      handleOptionClick(
                        option.id
                      )
                    }
                  />
                );
              }
            )}
          </DroppableOptionsPool>
        </div>
      </div>

      {typeof document !==
        'undefined' &&
        createPortal(
          <DragOverlay
            dropAnimation={null}
            zIndex={10000}
          >
            {activeOption ? (
              <div className="formula-dnd-drag-overlay">
                {
                  activeOption.label
                }
              </div>
            ) : null}
          </DragOverlay>,
          document.body
        )}
    </DndContext>
  );
};

export default FormulaDragAndDrop;